import React, { useCallback, useEffect, useState } from "react";
import { Server, ShieldCheck, RefreshCw, Store, Lock } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import Layout from "@/components/Layout";
import McpKeys from "@/components/McpKeys";
import { Card, Button, Chip, Skeleton, EmptyState } from "@/ui";
import { manilaDateTime } from "@/lib/format";

const SERVERS = [
  { key: "shopfront", Icon: Store, title: "shopfront",
    body: "Attached to the assistant that talks to strangers. Read and build-your-own-basket only — the seven customer tools. It cannot refund, reprice, edit stock, read the customer list or message anyone." },
  { key: "backoffice", Icon: Lock, title: "back office",
    body: "Every other feature a person can do on a screen. Anything that moves money, changes a price or messages someone stops for a person to approve." },
  { key: "platform", Icon: Server, title: "platform",
    body: "Platform-owner features: shops, plans and take rates. Money changes stop for approval." },
];

const OUTCOME_TONE = {
  ok: "good", approved_executed: "good", needs_approval: "warn",
  refused: "bad", error: "bad", unknown_tool: "bad",
};

// Platform Owner: one MCP server per shop instead of many function definitions.
// See the three servers, mint the platform key, and watch every call.
export default function McpServers() {
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/mcp/logs")
      .then((r) => setLogs(r.data.logs || []))
      .catch((e) => setError(friendlyError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  return (
    <Layout>
      <div className="row-between" style={{ marginBottom: 16 }}>
        <h1 className="t-h1">mcp servers</h1>
        <Button data-testid="refresh-logs-btn" onClick={load}><RefreshCw size={16} strokeWidth={1.75} />refresh</Button>
      </div>
      <p className="muted" style={{ marginBottom: 20, maxWidth: 680 }}>
        Reto speaks MCP in both directions. Each shop gets its own set of servers, reached by its own key.
        Every ability lives once and is served the same way over MCP and over plain custom functions.
      </p>

      <div className="grid grid-3" style={{ marginBottom: 16, alignItems: "start" }}>
        {SERVERS.map(({ key, Icon, title, body }) => (
          <Card key={key} data-testid={`mcp-server-${key}`}>
            <div className="row" style={{ gap: 8, marginBottom: 10 }}>
              <span className="ph-icon" style={{ margin: 0 }}><Icon size={18} strokeWidth={1.75} /></span>
              <h2 className="t-h3">{title}</h2>
            </div>
            <p className="muted t-small">{body}</p>
          </Card>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <McpKeys platform />
      </div>

      <Card data-testid="mcp-logs-card">
        <div className="row" style={{ gap: 10, marginBottom: 12 }}>
          <span className="ph-icon" style={{ margin: 0 }}><ShieldCheck size={18} strokeWidth={1.75} /></span>
          <h2 className="t-h2">recent calls</h2>
        </div>
        {error ? <div className="err-text" data-testid="mcp-logs-error" style={{ marginBottom: 12 }}>{error}</div> : null}
        {loading || logs === null ? (
          <Skeleton variant="card" />
        ) : logs.length === 0 ? (
          <EmptyState icon={<Server size={20} strokeWidth={1.75} />} title="no calls yet" />
        ) : (
          <div className="tbl-wrap" data-testid="mcp-logs-table">
            <table className="tbl">
              <thead><tr><th>When</th><th>Server</th><th>Shop</th><th>Tool</th><th>Outcome</th><th>ms</th></tr></thead>
              <tbody>
                {logs.map((l, i) => (
                  <tr key={l.id || i} data-testid={`mcp-log-${i}`}>
                    <td className="muted-3 t-small">{l.at ? manilaDateTime(l.at) : "\u2014"}</td>
                    <td className="t-small">{l.server}</td>
                    <td className="t-small mono">{l.shopId || "\u2014"}</td>
                    <td className="t-small">{l.tool || "\u2014"}</td>
                    <td><Chip tone={OUTCOME_TONE[l.outcome] || "neutral"} dot>{l.outcome}{l.reason ? ` · ${l.reason}` : ""}</Chip></td>
                    <td className="num t-small">{l.ms ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </Layout>
  );
}
